import { COUNTRIES } from "../../../../utils/constants";

const isKnownCountry = (value) => COUNTRIES.some((opt) => opt.value === value);

const apiValueToFormValue = (apiValue) => {
  const normalized = (apiValue || "").trim();
  if (!normalized) return "";
  const found = COUNTRIES.find(
    (c) => c.value.toLowerCase() === normalized.toLowerCase()
  );
  return found ? found.value : normalized;
};

const formValueToApiValue = (formValue) => {
  if (!formValue) return "";
  if (isKnownCountry(formValue)) {
    const country = COUNTRIES.find(
      (c) => c.value.toLowerCase() === (formValue || "").toLowerCase()
    );
    return country ? country.value : formValue;
  }
  return formValue;
};

const buildProductImageItem = ({ imageUrl, title, description }) => {
  const item = { imageUrl: imageUrl.trim() };

  if (title?.trim()) {
    item.title = title.trim();
  }

  if (description?.trim()) {
    item.description = description.trim();
  }

  return item;
};

export const normalizeProductImagesForForm = (items) => {
  if (!Array.isArray(items)) return [];

  return items
    .filter((item) => item?.imageUrl?.trim())
    .map((item) =>
      buildProductImageItem({
        imageUrl: item.imageUrl,
        title: item.title,
        description: item.description,
      })
    );
};

export const needsProductImageMetadata = (items) => {
  return normalizeProductImagesForForm(items).some(
    (item) => item.imageUrl && !item.title?.trim() && !item.description?.trim()
  );
};

export const MAX_ADDITIONAL_ASSETS = 10;

const buildAdditionalAssetItem = ({
  urlImage,
  prompt,
  isConditional,
  variableInfo,
  variantGroup,
}) => ({
  urlImage: urlImage.trim(),
  prompt: prompt.trim(),
  isConditional: !!isConditional,
  variableInfo: !!variableInfo,
  variantGroup: typeof variantGroup === "string" ? variantGroup.trim() : "",
});

export const collectUniqueVariantGroups = (
  items = [],
  { excludeIndex = null } = {}
) => {
  if (!Array.isArray(items)) return [];

  const groups = new Set();
  items.forEach((item, index) => {
    if (excludeIndex != null && index === excludeIndex) {
      return;
    }
    const group = item?.variantGroup?.trim();
    if (group) {
      groups.add(group);
    }
  });

  return Array.from(groups).sort((a, b) => a.localeCompare(b));
};

export const normalizeAdditionalAssetsForForm = (items) => {
  if (!Array.isArray(items)) return [];

  return items
    .filter((item) => item?.urlImage?.trim() && item?.prompt?.trim())
    .slice(0, MAX_ADDITIONAL_ASSETS)
    .map((item) =>
      buildAdditionalAssetItem({
        urlImage: item.urlImage,
        prompt: item.prompt || "",
        isConditional: item.isConditional,
        variableInfo: item.variableInfo,
        variantGroup: item.variantGroup,
      })
    );
};

export const mergeBusinessProductImagesOnSave = ({ formItems, dbItems }) => {
  const normalizedFormItems = normalizeProductImagesForForm(formItems);
  const normalizedDbItems = normalizeProductImagesForForm(dbItems);

  return normalizedFormItems.map((formItem) => {
    const existing = normalizedDbItems.find(
      (dbItem) => dbItem.imageUrl === formItem.imageUrl
    );

    const title = formItem.title?.trim() || existing?.title?.trim();
    const description =
      formItem.description?.trim() || existing?.description?.trim();

    return buildProductImageItem({
      imageUrl: formItem.imageUrl,
      title,
      description,
    });
  });
};

const normalizeTypography = (typography, currentTypography = null) => {
  const defaultTypography = {
    fontFamily: "Inter",
    subheadingFontFamily: "",
    bodyFontFamily: "",
    sizes: {
      heading: "48px",
      subheading: "24px",
      body: "16px",
    },
  };

  if (!typography || typeof typography !== "object") {
    return currentTypography || defaultTypography;
  }

  return {
    fontFamily:
      typography.fontFamily ||
      currentTypography?.fontFamily ||
      defaultTypography.fontFamily,
    subheadingFontFamily: typography.subheadingFontFamily || "",
    bodyFontFamily: typography.bodyFontFamily || "",
    sizes: {
      heading:
        typography.sizes?.heading ||
        currentTypography?.sizes?.heading ||
        defaultTypography.sizes.heading,
      subheading:
        typography.sizes?.subheading ||
        currentTypography?.sizes?.subheading ||
        defaultTypography.sizes.subheading,
      body:
        typography.sizes?.body ||
        currentTypography?.sizes?.body ||
        defaultTypography.sizes.body,
    },
  };
};

export const normalizeApiDataToForm = (apiData) => {
  if (!apiData) return getDefaultFormValues();

  return {
    businessName: apiData.businessName || "",
    businessDescription: apiData.businessDescription || "",
    websiteUrl: apiData.websiteUrl || apiData.correctUrl || "",
    businessCompetitors: normalizeArrayField(apiData.businessCompetitors),
    businessOffering: apiData.businessOffering || "",
    businessLookAndFeel: apiData.businessLookAndFeel || "",
    businessValueProposition: apiData.businessValueProposition || "",
    businessIdealCustomerProfile: apiData.businessIdealCustomerProfile || "",
    businessBuyerPerson: Array.isArray(apiData.businessBuyerPerson)
      ? apiData.businessBuyerPerson
      : [],
    businessTextStyle: apiData.businessTextStyle || "",
    businessVoiceAttributes: Array.isArray(apiData.businessVoiceAttributes)
      ? apiData.businessVoiceAttributes
      : [],
    logoUrl: apiData.logoUrl || "",
    businessProductImages: normalizeProductImagesForForm(
      apiData.businessProductImages
    ),
    additionalAssets: normalizeAdditionalAssetsForForm(
      apiData.additionalAssets
    ),
    businessCountry: apiValueToFormValue(apiData.businessCountry),
    socialNetworks: normalizeArrayField(apiData.socialNetworks),
    businessColors: Array.isArray(apiData.businessColors)
      ? apiData.businessColors
      : [],
    businessTypography: normalizeTypography(apiData.businessTypography),
    businessTypographyPreview: apiData.businessTypographyPreview || "",
    newSocialUrl: "",
    newCompetitor: "",
    newVoiceAttribute: "",
  };
};

export const normalizeFormDataToApi = (formData) => {
  const apiData = {
    websiteUrl: formData.websiteUrl || "",
    socialNetworks: filterEmptyArrayItems(formData.socialNetworks),
    businessName: formData.businessName || "",
    businessDescription: formData.businessDescription || "",
    businessTextStyle: formData.businessTextStyle || "",
    businessVoiceAttributes: Array.isArray(formData.businessVoiceAttributes)
      ? formData.businessVoiceAttributes
      : [],
    businessOffering: formData.businessOffering || "",
    businessLookAndFeel: formData.businessLookAndFeel || "",
    businessValueProposition: formData.businessValueProposition || "",
    businessIdealCustomerProfile: formData.businessIdealCustomerProfile || "",
    businessBuyerPerson: filterEmptyBuyerPersonas(formData.businessBuyerPerson),
    logoUrl: formData.logoUrl || "",
    businessProductImages: normalizeProductImagesForForm(
      formData.businessProductImages
    ),
    additionalAssets: normalizeAdditionalAssetsForForm(
      formData.additionalAssets
    ),
    businessCountry: formValueToApiValue(formData.businessCountry),
    businessCompetitors: filterEmptyArrayItems(formData.businessCompetitors),
    businessNameLowercase: getBusinessNameLowercase(formData.businessName),
  };

  if (
    Array.isArray(formData.businessColors) &&
    formData.businessColors.length > 0
  ) {
    apiData.businessColors = formData.businessColors;
  }

  if (
    formData.businessTypography &&
    typeof formData.businessTypography === "object"
  ) {
    const typography = formData.businessTypography;
    apiData.businessTypography = {
      fontFamily: typography.fontFamily || "Inter",
      sizes: {
        heading: typography.sizes?.heading || "48px",
        subheading: typography.sizes?.subheading || "24px",
        body: typography.sizes?.body || "16px",
      },
    };

    if (typography.bodyFontFamily?.trim()) {
      apiData.businessTypography.bodyFontFamily =
        typography.bodyFontFamily.trim();
    }
  }

  if (formData.businessTypographyPreview) {
    apiData.businessTypographyPreview = formData.businessTypographyPreview;
  }

  return apiData;
};

export const getDefaultFormValues = () => ({
  websiteUrl: "",
  businessName: "",
  businessDescription: "",
  businessTextStyle: "",
  businessVoiceAttributes: [],
  businessCompetitors: [],
  logoUrl: "",
  businessProductImages: [],
  additionalAssets: [],
  businessCountry: "",
  socialNetworks: [],
  businessColors: [],
  businessLookAndFeel: "",
  businessValueProposition: "",
  businessOffering: "",
  businessIdealCustomerProfile: "",
  businessBuyerPerson: [],
  businessTypography: {
    fontFamily: "Inter",
    subheadingFontFamily: "",
    bodyFontFamily: "",
    sizes: {
      heading: "48px",
      subheading: "24px",
      body: "16px",
    },
  },
  businessTypographyPreview: "",
  newSocialUrl: "",
  newCompetitor: "",
  newVoiceAttribute: "",
});

export const someImportantDataExtractedIsEmpty = (data = {}) => {
  const {
    businessName,
    businessDescription,
    businessTextStyle,
    businessVoiceAttributes,
    businessCompetitors,
    businessColors,
    businessLookAndFeel,
    businessValueProposition,
    businessOffering,
    businessTypography,
  } = data;
  return (
    !businessName ||
    !businessDescription ||
    !businessTextStyle ||
    !businessVoiceAttributes ||
    !businessCompetitors ||
    !businessColors ||
    !businessLookAndFeel ||
    !businessValueProposition ||
    !businessOffering ||
    !businessTypography
  );
};

export const normalizeArrayField = (field) => {
  if (!Array.isArray(field)) return [];
  const filtered = field.filter((item) => item && item.trim() !== "");
  return filtered.length > 0 ? filtered : [];
};

const filterEmptyArrayItems = (array) => {
  if (!Array.isArray(array)) return [];
  return array.filter((item) => item && item.trim() !== "");
};

const filterEmptyBuyerPersonas = (array) => {
  if (!Array.isArray(array)) return [];
  return array.filter((item) => {
    if (!item || typeof item !== "object") return false;

    const name = (item.name || "").trim();
    const text = (item.text || "").trim();

    return name !== "" || text !== "";
  });
};

const getBusinessNameLowercase = (businessName) =>
  (businessName || "").toLowerCase().trim();
